import React, { useState, useEffect, useContext } from 'react';
import {
    Container,
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardHeader,
    Grid,
    Chip,
    Avatar,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    TextField
} from '@mui/material';
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Badge as BadgeIcon,
    CreditCard as CreditCardIcon,
    Storage as StorageIcon,
    Schedule as ScheduleIcon,
    Upgrade as UpgradeIcon,
    Cancel as CancelIcon,
    Edit as EditIcon,
    Receipt as ReceiptIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Settings as SettingsIcon,
    Save as SaveIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { userContext } from '../../stores/users/userContext';
import { messageContext } from '../../stores/messages/messageContext';
import Footer from '../footer/footer';
import { getStatusColor , formatDate, capitalize} from '../../utils';
import { observer } from 'mobx-react-lite';

const Profile = () => {
    const [loading, setLoading] = useState(true);
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [usageData, setUsageData] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [cancelDialog, setCancelDialog] = useState(false);
    const [editDialog, setEditDialog] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editData, setEditData] = useState({
        full_name: '',
        nickname: ''
    });
    const userStore = useContext(userContext);
    const messageStore = useContext(messageContext);
    const navigate = useNavigate();


    const handleEditProfile = () => {
        setEditData({
            full_name: userStore.getUserFullName() || '',
            nickname: userStore.getUserNickname() || ''
        });
        setEditDialog(true);
    };

    const handleSaveProfile = async () => {
        setEditLoading(true);
        try {
            const formData = new FormData();
            formData.append('token', userStore.getToken());
            formData.append('full_name', editData.full_name);
            formData.append('nickname', editData.nickname);

            await userStore.updateUserProfile(formData);
            setEditDialog(false);
            messageStore.setMessage('Profile updated successfully!', 'success');
        } catch (err) {
            console.error('Profile update error:', err);
            messageStore.setMessage('Failed to update profile. Please try again.', 'error');
        } finally {
            setEditLoading(false);
        }
    };

    const handleEditInputChange = (field: string, value: string) => {
        setEditData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const getStoragePercentage = () => {
        if (!usageData || !usageData.storage_limit) return 0;
        return Math.min((usageData.storage_used / usageData.storage_limit) * 100, 100);
    };

    const getTranscriptionPercentage = () => {
        if (!usageData || !usageData.transcription_hours_limit) return 0;
        return Math.min((usageData.transcription_hours_used / usageData.transcription_hours_limit) * 100, 100);
    };

  

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{
                mt: { xs: '70px', sm: '75px', md: '61px' },
                mb: { xs: 4, sm: 6, md: 8 },
                px: { xs: 2, sm: 3, md: 0 }
            }}>
                <Typography
                    variant="h3"
                    component="h1"
                    gutterBottom
                    fontWeight="bold"
                    sx={{
                        fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                        mb: 4, 
                        textAlign: 'center'
                    }}
                >
                    Profile
                </Typography>

                <Grid container spacing={4}>
                    {/* User Information Card */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Card sx={{ height: '100%' }}>
                            <CardHeader
                                title="User Information"
                                action={
                                    <Tooltip title="Edit Profile">
                                        <IconButton onClick={handleEditProfile}>
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                }
                            />
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Avatar
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            bgcolor: 'primary.main',
                                            fontSize: '1.5rem',
                                            mr: 2
                                        }}
                                    >
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" component="div">
                                            Name: {userStore.getUserFullName() || 'Welcome!'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Nickname: {userStore.getUserNickname() || 'User'}
                                        </Typography>
                                    </Box>
                                </Box>

                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>
                                            <EmailIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Email:"
                                            secondary={userStore.getUserEmail()}
                                        />
                                    </ListItem>
                                   
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Current Plan Card */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Card sx={{ height: '100%' }}>
                            <CardHeader
                                title="Current Plan"
                                action={
                                    <Chip
                                        label={userStore.getUserPlan() || 'Free'}
                                        color={userStore.isUserStripeActive() ? 'success' : 'default'}
                                        variant={userStore.isUserStripeActive() ? 'filled' : 'outlined'}
                                    />
                                }
                            />
                            <CardContent>
                                {userStore.isUserStripeActive() && subscriptionData?.subscription ? (
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                                            <Typography variant="body1" fontWeight="medium">
                                                Active Subscription
                                            </Typography>
                                        </Box>
                                        
                                        <List dense>
                                            <ListItem>
                                                <ListItemText
                                                    primary="Status"
                                                    secondary={
                                                        <Chip
                                                            label={subscriptionData.subscription.status}
                                                            color={getStatusColor(subscriptionData.subscription.status)}
                                                            size="small"
                                                        />
                                                    }
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText
                                                    primary="Next Billing"
                                                    secondary={formatDate(subscriptionData.subscription.current_period_end)}
                                                />
                                            </ListItem>
                                            {subscriptionData.subscription.cancel_at_period_end && (
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <WarningIcon color="warning" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary="Cancellation Scheduled"
                                                        secondary="Will end at period end"
                                                    />
                                                </ListItem>
                                            )}
                                        </List>

                                        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexDirection: 'column' }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<SettingsIcon />}
                                                onClick={() => navigate('/subscription-management/')}
                                                size="small"
                                            >
                                                Manage Subscription
                                            </Button>
                                            {!subscriptionData.subscription.cancel_at_period_end && (
                                                <Button
                                                    variant="outlined"
                                                    color="warning"
                                                    startIcon={<CancelIcon />}
                                                    onClick={() => setCancelDialog(true)}
                                                    size="small"
                                                >
                                                    Cancel Subscription
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <InfoIcon color="info" sx={{ mr: 1 }} />
                                            <Typography variant="body1" fontWeight="medium">
                                                Free Plan
                                            </Typography>
                                        </Box>
                                        
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            You're currently on the free plan. Upgrade to unlock premium features!
                                        </Typography>

                                        <Button
                                            variant="contained"
                                            startIcon={<UpgradeIcon />}
                                            onClick={() => navigate('/subscription/')}
                                            fullWidth
                                        >
                                            Upgrade Plan
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Usage Stats Card */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Card sx={{ height: '100%' }}>
                            <CardHeader title="Usage Statistics" />
                            <CardContent>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>
                                            <StorageIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Storage Used"
                                            secondary={
                                                <Box>
                                                    <Typography variant="body2">
                                                        {usageData?.storage_used || 0} GB
                                                        {usageData?.storage_limit && ` / ${usageData.storage_limit} GB`}
                                                    </Typography>
                                                    {usageData?.storage_limit && (
                                                        <Box sx={{ mt: 1 }}>
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    height: 6,
                                                                    bgcolor: 'grey.300',
                                                                    borderRadius: 3,
                                                                    overflow: 'hidden'
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: `${getStoragePercentage()}%`,
                                                                        height: '100%',
                                                                        bgcolor: getStoragePercentage() > 80 ? 'error.main' : 'primary.main',
                                                                        transition: 'width 0.3s ease'
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <ScheduleIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Transcription Hours"
                                            secondary={
                                                <Box>
                                                    <Typography variant="body2">
                                                        {usageData?.transcription_hours_used || 0} hours
                                                        {usageData?.transcription_hours_limit && ` / ${usageData.transcription_hours_limit} hours`}
                                                    </Typography>
                                                    {usageData?.transcription_hours_limit && (
                                                        <Box sx={{ mt: 1 }}>
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    height: 6,
                                                                    bgcolor: 'grey.300',
                                                                    borderRadius: 3,
                                                                    overflow: 'hidden'
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: `${getTranscriptionPercentage()}%`,
                                                                        height: '100%',
                                                                        bgcolor: getTranscriptionPercentage() > 80 ? 'error.main' : 'primary.main',
                                                                        transition: 'width 0.3s ease'
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <ReceiptIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Total Files"
                                            secondary={`${usageData?.files_count || 0} files processed`}
                                        />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Payment Information Card */}
                    {userStore.isUserStripeActive() && (
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardHeader title="Payment Information" />
                                <CardContent>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon>
                                                <CreditCardIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Stripe Customer ID"
                                                secondary={userStore.getUserStripeCustomerId()}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon>
                                                <ReceiptIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Subscription ID"
                                                secondary={userStore.getUserStripeSubscriptionId()}
                                            />
                                        </ListItem>
                                    </List>

                                    <Box sx={{ mt: 2 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<CreditCardIcon />}
                                            fullWidth
                                            onClick={() => navigate('/subscription-management/')}
                                        >
                                            Update Payment Method
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                                         
                </Grid>

                {/* Edit Profile Dialog */}
                <Dialog
                    open={editDialog}
                    onClose={() => setEditDialog(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <EditIcon color="primary" sx={{ mr: 1 }} />
                            Edit Profile
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                label="Full Name"
                                value={editData.full_name}
                                onChange={(e) => handleEditInputChange('full_name', e.target.value)}
                                margin="normal"
                                variant="outlined"
                                helperText="Your full name as it will appear on your profile"
                            />
                            <TextField
                                fullWidth
                                label="Nickname"
                                value={editData.nickname}
                                onChange={(e) => handleEditInputChange('nickname', e.target.value)}
                                margin="normal"
                                variant="outlined"
                                helperText="A friendly name others can see"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button 
                            onClick={() => setEditDialog(false)}
                            startIcon={<CloseIcon />}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveProfile}
                            variant="contained"
                            disabled={editLoading}
                            startIcon={editLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                        >
                            {editLoading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Footer />
            </Box>
        </Container>
    );
};

export default observer(Profile);